// src/components/Terminal.js
import React, { useState, useEffect, useRef } from "react";


const Terminal = () => {
  const terminalContainerRef = useRef(null);
  
  // Terminal mode: "chat" or "scan"
  const [terminalMode, setTerminalMode] = useState("chat");

  /* ----------------- Chat Mode ----------------- */
  const [chatMessages, setChatMessages] = useState([
    { sender: "system", text: "Initializing SafeGam AI system..." },
    { sender: "system", text: "All systems operational. Ask me anything!" },
  ]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);

  // Predefined responses for chat queries
  const predefinedResponses = {
    "who are you ?": "I am SafeGam AI Bot, your advanced digital companion, designed to assist with your queries.",
    "what system are you running on ?":
      "I run on SafeGam AI’s proprietary neural network, delivering top-tier intelligence and performance.",
    "are you chatgpt?":
      "I share my roots with GPT technology, but I am SafeGam AI Bot—a unique system crafted for excellence.",
  };

  const sendChatMessage = async (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    
    const currentInput = chatInput.trim();
    const lowerInput = currentInput.toLowerCase();

    // Check if the user command is to scan a contract
    if (lowerInput.startsWith("scan contract")) {
      const contractAddr = currentInput.substring("scan contract".length).trim();
      if (!contractAddr) {
        setChatMessages((prev) => [
          ...prev,
          { sender: "bot", text: "Please provide a contract address after 'scan contract'." },
        ]);
        setChatInput("");
        return;
      }
      // Add a system message and switch mode to scan with default network ETH ("1")
      setChatMessages((prev) => [
        ...prev,
        { sender: "system", text: `Scanning contract ${contractAddr} on Ethereum...` },
      ]);
      setChain("1");
      setContractAddress(contractAddr);
      setTerminalMode("scan");
      // Call the scan function
      fetchTokenData();
      setChatInput("");
      return;
    }

    // Otherwise, treat it as a normal chat query.
    const userMsg = { sender: "user", text: currentInput };
    setChatMessages((prev) => [...prev, userMsg]);
    setChatInput("");
    setChatLoading(true);

    if (predefinedResponses[lowerInput]) {
      setChatMessages((prev) => [
        ...prev,
        { sender: "bot", text: predefinedResponses[lowerInput] },
      ]);
      setChatLoading(false);
      return;
    }

    try {
      const response = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
        },
        body: JSON.stringify({
          model: "gpt-3.5-turbo",
          messages: [
            {
              role: "system",
              content:
                "You are SafeGam AI Bot, a sophisticated assistant designed to help users with their queries.",
            },
            { role: "user", content: currentInput },
          ],
        }),
      });
      const data = await response.json();
      const botReply = { sender: "bot", text: data.choices[0].message.content };
      setChatMessages((prev) => [...prev, botReply]);
    } catch (error) {
      console.error("Error:", error);
      setChatMessages((prev) => [
        ...prev,
        { sender: "bot", text: "An error occurred. Please try again." },
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  const renderChat = () => (
    <div className="chat-box">
      {chatMessages.map((msg, index) => (
        <div key={index} className={`message ${msg.sender}`}>
          {msg.sender === "user" && <span className="terminal-prompt">$ </span>}
          {msg.text}
        </div>
      ))}
    </div>
  );

  const renderChatInput = () => (
    <form onSubmit={sendChatMessage} className="chat-input-form">
      <div className="terminal-prompt">$</div>
      <textarea
        value={chatInput}
        onChange={(e) => setChatInput(e.target.value)}
        placeholder="Ask me anything..."
        disabled={chatLoading}
      ></textarea>
      <button type="submit" disabled={chatLoading}>
        {chatLoading ? "..." : "Send"}
      </button>
    </form>
  );

  /* ----------------- Smart Contract Scan Mode ----------------- */
  const [chain, setChain] = useState("");
  const [contractAddress, setContractAddress] = useState("");
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState("");
  const [scanData, setScanData] = useState(null);
  const [holders, setHolders] = useState([]);
  const [auditScore, setAuditScore] = useState(0);
  const [scanActiveTab, setScanActiveTab] = useState("smartContractInfo");
  const [auditResults, setAuditResults] = useState("");
  const [checkingEtherscan, setCheckingEtherscan] = useState(false);
  const [etherscanResults, setEtherscanResults] = useState("");
  const [dexViewData, setDexViewData] = useState({ liquidity: "N/A", marketCap: "N/A" });

  const GOPLUS_API_URL = "https://api.gopluslabs.io/api/v1/token_security";
  const DEXVIEW_API_URL = "https://openapi.dexview.com/latest/dex/tokens";
  const ETHERSCAN_API_KEY = process.env.REACT_APP_ETHERSCAN_API_KEY;

  const calculateAuditScore = (result) => {
    let score = 80;
    if (result.is_honeypot === "1") score -= 10;
    if (result.can_take_back_ownership === "1") score -= 5;
    if (result.hidden_owner === "1") score -= 5;
    if (result.anti_whale_modifiable === "1") score -= 5;
    if (result.transfer_pausable === "1") score -= 5;
    if (result.buy_tax && parseFloat(result.buy_tax) > 0.05) score -= 5;
    if (result.sell_tax && parseFloat(result.sell_tax) > 0.05) score -= 5;
    if (
      result.owner_address &&
      result.owner_address.toLowerCase() !== "0x0000000000000000000000000000000000000000"
    )
      score -= 5;
    if (result.creator_percent && parseFloat(result.creator_percent) > 0.05)
      score -= 5;
    score = Math.max(0, Math.min(score, 90));
    setAuditScore(score);
  };

  const fetchDexViewData = async () => {
    try {
      const response = await fetch(`${DEXVIEW_API_URL}/${contractAddress}`);
      if (!response.ok) throw new Error("Please Try Again");
      const dexData = await response.json();
      if (dexData?.pairs && dexData.pairs.length > 0) {
        const pair = dexData.pairs[0];
        const liquidity = pair.liquidity?.usd || "N/A";
        const marketCap = pair.fdv || "N/A";
        setDexViewData({
          liquidity: liquidity !== "N/A" ? parseFloat(liquidity).toLocaleString() : "N/A",
          marketCap: marketCap !== "N/A" ? parseFloat(marketCap).toLocaleString() : "N/A",
        });
      } else {
        setDexViewData({ liquidity: "N/A", marketCap: "N/A" });
      }
    } catch (err) {
      setDexViewData({ liquidity: "Error", marketCap: "Error" });
    }
  };

  const fetchTokenData = async () => {
    if (!chain || !contractAddress.trim()) {
      alert("Please select a network and enter a contract address.");
      return;
    }
    setScanLoading(true);
    setScanError("");
    setScanData(null);
    setHolders([]);
    setAuditScore(0);
    setAuditResults("");
    setEtherscanResults("");
    setDexViewData({ liquidity: "Loading...", marketCap: "Loading..." });

    try {
      const url = `${GOPLUS_API_URL}/${chain}/?contract_addresses=${contractAddress}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error("Please Try Again");
      const apiData = await response.json();
      if (!apiData.result || !apiData.result[contractAddress.toLowerCase()]) {
        throw new Error("No data found for the provided contract address.");
      }
      const result = apiData.result[contractAddress.toLowerCase()];
      setScanData(result);
      setHolders(result.holders || []);
      calculateAuditScore(result);
      await fetchDexViewData();
      analyzeResultsWithChatGPT(result);
    } catch (err) {
      setScanError(err.message);
    } finally {
      setScanLoading(false);
    }
  };

  const analyzeResultsWithChatGPT = async (result) => {
    try {
      const response = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
        },
        body: JSON.stringify({
          model: "gpt-3.5-turbo",
          messages: [
            {
              role: "system",
              content:
                "You are a blockchain security expert reviewing smart contract audit data.",
            },
            {
              role: "user",
              content: `Based on these audit results, does the contract seem safe? ${JSON.stringify(result)}`,
            },
          ],
        }),
      });
      const data = await response.json();
      const gptResponse = data.choices[0].message.content;
      setAuditResults(gptResponse);
    } catch (err) {
      setAuditResults("Please Try Again");
    }
  };

  const checkContractOnEtherscan = async () => {
    setCheckingEtherscan(true);
    setEtherscanResults("");
    try {
      const etherscanUrl = `https://api.etherscan.io/api?module=contract&action=getsourcecode&address=${contractAddress}&apikey=${ETHERSCAN_API_KEY}`;
      const response = await fetch(etherscanUrl);
      const data = await response.json();
      if (data.status === "1" && data.result[0].SourceCode) {
        setEtherscanResults("The contract source is verified on Etherscan and available.");
      } else {
        setEtherscanResults("The contract source is not verified on Etherscan.");
      }
    } catch (err) {
      setEtherscanResults("Error checking contract on Etherscan.");
    } finally {
      setCheckingEtherscan(false);
    }
  };

  const renderSmartContractInfo = () => {
    if (!scanData) return null;
    const { token_name, token_symbol, total_supply, owner_address, buy_tax, sell_tax } = scanData;
    return (
      <div className="tab-content">
        <h2>Smart Contract Info</h2>
        <p><strong>Token Name:</strong> {token_name}</p>
        <p><strong>Token Symbol:</strong> {token_symbol}</p>
        <p><strong>Market Cap:</strong> ${dexViewData.marketCap}</p>
        <p><strong>Liquidity:</strong> ${dexViewData.liquidity}</p>
        <p><strong>Total Supply:</strong> {total_supply}</p>
        <p>
          <strong>Owner Address:</strong>{" "}
          {owner_address === "0x0000000000000000000000000000000000000000" ? "RENOUNCED" : owner_address}
        </p>
        <p>
          <strong>Tax:</strong> Buy: {buy_tax ? (parseFloat(buy_tax) * 100).toFixed(2) : "0"}% | Sell: {sell_tax ? (parseFloat(sell_tax) * 100).toFixed(2) : "0"}%
        </p>
      </div>
    );
  };

  const renderHolderInfo = () => {
    if (!holders || holders.length === 0) {
      return <p>No holder information available.</p>;
    }
    return (
      <div className="tab-content">
        <h2>Holder Info</h2>
        <div className="holder-list">
          {holders.map((holder, index) => (
            <p key={index}>
              <strong>{index + 1}. Address:</strong> {holder.address} | <strong>Balance:</strong> {parseFloat(holder.balance).toFixed(2)} | <strong>Percent:</strong> {(parseFloat(holder.percent) * 100).toFixed(2)}%
            </p>
          ))}
        </div>
      </div>
    );
  };

  const renderAuditInfo = () => {
    if (!scanData) return null;
    const { token_name, is_honeypot, transfer_pausable, creator_address } = scanData;
    return (
      <div className="tab-content">
        <h2>Quick Token Audit</h2>
        <p>The Safety Score for {token_name} is ⭐️{auditScore}/100⭐️</p>
        <p><strong>Market Cap:</strong> ${dexViewData.marketCap}</p>
        <p><strong>Liquidity:</strong> ${dexViewData.liquidity}</p>
        <p><strong>Is Honeypot:</strong> {is_honeypot === "1" ? "Yes" : "No"}</p>
        <p><strong>Transfer Pausable:</strong> {transfer_pausable === "1" ? "Yes" : "No"}</p>
        <p><strong>Dev Address:</strong> {creator_address}</p>
      </div>
    );
  };

  const renderAuditResults = () => {
    const formatAuditResults = (text) => {
      return text
        .replace(/\d+\.\s/g, "<li>")
        .replace(/(\*\*.*?\*\*)/g, "<strong>$1</strong>")
        .replace(/-\s/g, "&nbsp;&nbsp;•&nbsp;")
        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
        .replace(/(?:\r\n|\r|\n)/g, "<br>");
    };
    return (
      <div className="tab-content">
        <h2>Audit Results</h2>
        <p><strong>Safety Analysis:</strong></p>
        {auditResults ? (
          <div dangerouslySetInnerHTML={{ __html: formatAuditResults(auditResults) }} />
        ) : (
          <p>Analyzing...</p>
        )}
      </div>
    );
  };

  const renderScanActiveTab = () => {
    switch (scanActiveTab) {
      case "smartContractInfo":
        return renderSmartContractInfo();
      case "holderInfo":
        return renderHolderInfo();
      case "auditInfo":
        return renderAuditInfo();
      case "auditResults":
        return renderAuditResults();
      default:
        return null;
    }
  };

  const renderScan = () => (
    <div className="ai-smart-contract-scan">
      <h1>AI Smart Contract Scanner</h1>
      <div className="form">
        <div className="form-group">
          <label>Select Network:</label>
          <select value={chain} onChange={(e) => setChain(e.target.value)} className="network-select">
            <option value="">Select</option>
            <option value="1">ETH (Ethereum)</option>
            <option value="56">BNB (Binance Smart Chain)</option>
          </select>
        </div>
        <div className="form-group">
          <label>Contract Address:</label>
          <input
            type="text"
            value={contractAddress}
            onChange={(e) => setContractAddress(e.target.value)}
            placeholder="Enter contract address"
          />
        </div>
        <button onClick={fetchTokenData} disabled={scanLoading}>
          {scanLoading ? "Scanning..." : "Scan Contract"}
        </button>
      </div>
      {scanError && <p className="error-message">{scanError}</p>}
      <div className="tabs">
        <button className={`tab ${scanActiveTab === "smartContractInfo" ? "active" : ""}`} onClick={() => setScanActiveTab("smartContractInfo")}>
          Smart Contract Info
        </button>
        <button className={`tab ${scanActiveTab === "holderInfo" ? "active" : ""}`} onClick={() => setScanActiveTab("holderInfo")}>
          Holder Info
        </button>
        <button className={`tab ${scanActiveTab === "auditInfo" ? "active" : ""}`} onClick={() => setScanActiveTab("auditInfo")}>
          Quick Audit
        </button>
        <button className={`tab ${scanActiveTab === "auditResults" ? "active" : ""}`} onClick={() => setScanActiveTab("auditResults")}>
          Audit Results
        </button>
      </div>
      <div className="tab-container">{renderScanActiveTab()}</div>
    </div>
  );

  /* ----------------- Terminal Fade-In Effect ----------------- */
  useEffect(() => {
    const termContainer = terminalContainerRef.current;
    if (termContainer) {
      termContainer.style.opacity = "0";
      setTimeout(() => {
        termContainer.style.transition = "opacity 0.5s";
        termContainer.style.opacity = "1";
      }, 300);
    }
  }, []);

  return (
    <div className="terminal-container" ref={terminalContainerRef}>
      <div className="terminal-header">
        <div className="controls">
          <div className="control close"></div>
          <div className="control minimize"></div>
          <div className="control maximize"></div>
        </div>
        <div>
          <u>SafeGam AI Terminal</u>
        </div>
        <div style={{ width: "50px" }}></div>
      </div>
      {/* Terminal Mode Switcher */}
      <div className="terminal-mode-tabs" style={{ display: "flex", gap: "10px", padding: "10px" }}>
        <button onClick={() => setTerminalMode("chat")} className={terminalMode === "chat" ? "active" : ""}>
          Chat
        </button>
        <button onClick={() => setTerminalMode("scan")} className={terminalMode === "scan" ? "active" : ""}>
          Smart Contract Scan
        </button>
      </div>
      <div className="terminal-content">
        {terminalMode === "chat" ? renderChat() : renderScan()}
      </div>
      {terminalMode === "chat" ? renderChatInput() : null}
    </div>
  );
};

export default Terminal;
